import { useCallback } from 'react';
import { Modal, message } from 'antd';

import {
  cancelPublishTaskRun,
  executePublishTaskOnce,
  triggerPublishTaskOnce,
  updatePublishTask,
} from '../../api';
import { resolveErrorMessage } from '../../utils/errorMessage';

export const useTaskRunActions = ({
  activeRunsByTaskId,
  reloadListAfterUpdate,
  openRunViewer,
}) => {
  const handleTriggerOnce = useCallback((record) => {
    if (!record?.task_id) return;
    if (String(record?.status || '').toLowerCase() !== 'active') {
      message.warning('任务已停用/未启用，不能立即执行');
      return;
    }
    if (activeRunsByTaskId?.[record?.task_id]) {
      message.warning('任务正在运行，已阻止本次触发');
      return;
    }

    Modal.confirm({
      title: '立即执行',
      content: '将立即执行一次该发布任务（本次按“每账号 1 条”执行，不改变原定时规则）。确定要执行吗？',
      okText: '立即执行',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await triggerPublishTaskOnce({ task_id: record.task_id });
          if (resp?.status === 'accepted') {
            message.success('已触发一次运行');
            if (resp?.task_run_id) {
              openRunViewer(resp.task_run_id);
            }
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'conflict') {
            message.warning('任务正在运行，已阻止本次触发');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'forbidden') {
            message.warning('任务已停用/未启用，不能执行');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '执行失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  }, [activeRunsByTaskId, openRunViewer, reloadListAfterUpdate]);

  const handleExecuteOnce = useCallback((record) => {
    if (!record?.task_id) return;
    if (activeRunsByTaskId?.[record?.task_id]) {
      message.warning('任务正在运行，已阻止本次触发');
      return;
    }

    Modal.confirm({
      title: '立即执行',
      content: '将立即执行一次“手动批量发布”（单平台多账号，每账号 1 条）。确定要执行吗？',
      okText: '立即执行',
      cancelText: '取消',
      onOk: async () => {
        try {
          const platformCode = Array.isArray(record?.target_platform_codes) && record.target_platform_codes.length === 1
            ? record.target_platform_codes[0]
            : undefined;
          const resp = await executePublishTaskOnce({
            task_id: record.task_id,
            platform_code: platformCode,
            per_account_limit: 1,
          });
          if (resp?.status === 'accepted') {
            message.success('已触发一次执行');
            if (resp?.task_run_id) {
              openRunViewer(resp.task_run_id);
            }
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'conflict') {
            message.warning('任务正在运行，已阻止本次触发');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'forbidden') {
            message.warning('任务已停用/未启用，不能执行');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '执行失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  }, [activeRunsByTaskId, openRunViewer, reloadListAfterUpdate]);

  const handleCancelActiveRun = useCallback((record) => {
    const taskId = record?.task_id;
    if (!taskId) return;
    const active = activeRunsByTaskId?.[taskId];
    const runId = active?.run_id;
    if (!runId) {
      message.warning('未找到运行中的实例');
      return;
    }

    Modal.confirm({
      title: '停止运行',
      content: '将停止后续发布作业（未执行的会标记为 canceled）。正在执行中的发布可能仍会继续。确定要停止吗？',
      okText: '停止运行',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await cancelPublishTaskRun(runId);
          if (resp?.status === 'success') {
            message.success('已停止后续作业');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'ignored') {
            message.info('该运行已结束/不可停止');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '停止失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  }, [activeRunsByTaskId, reloadListAfterUpdate]);

  const handleToggleTaskStatus = useCallback((record) => {
    if (!record?.task_id) return;
    const current = String(record.status || '').toLowerCase();
    const next = current === 'active' ? 'paused' : 'active';
    const hasActiveRun = !!activeRunsByTaskId?.[record.task_id];

    Modal.confirm({
      title: next === 'paused' ? '停用任务' : '启用任务',
      content: next === 'paused' && hasActiveRun
        ? '当前任务存在运行中的实例；停用只会影响后续触发，不会中断当前运行。确定要停用吗？'
        : '确定要执行该操作吗？',
      okText: next === 'paused' ? '停用' : '启用',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await updatePublishTask({ task_id: record.task_id, status: next });
          if (resp?.status === 'success') {
            message.success(next === 'paused' ? '已停用' : '已启用');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'not_found') {
            message.error('任务不存在');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '更新失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  }, [activeRunsByTaskId, reloadListAfterUpdate]);

  return {
    handleTriggerOnce,
    handleExecuteOnce,
    handleCancelActiveRun,
    handleToggleTaskStatus,
  };
};
